'use client';

import { MotionConfig } from 'framer-motion';
import { useT } from '@/contexts/LanguageContext';
/* Import exactly what this page renders, and nothing else.
   Features, WhyAnotherERP, Advantage365, ComparisonTable, Infrastructure,
   FloatingWhatsApp and DemoSection were all imported here and never used —
   left behind as the page was re-composed. They are `"use client"` modules,
   so webpack has to treat them as side-effectful and cannot drop them: all
   seven were being bundled into the home page's client chunk and shipped to
   every visitor. They still exist and are still used by the pages that
   actually render them. */
import Hero from './Hero';

import ScrollLayout, { AnimatedSection } from './ScrollLayout';

import TheProblem from './TheProblem';
import WhyERPFail from './WhyERPFail';
import MultiSchool from './MultiSchool';
import TargetAudience from './TargetAudience';
import HowItWorks from './HowItWorks';
import FeaturedBlogs from '../blog/FeaturedBlogs';

import Header from './Header';
import Footer from './Footer';
import ScrollToHash from './ScrollToHash';
import Preloader from './Preloader';

import ROICalculator from './ROICalculator';
import ProductShowcase from './ProductShowcase';
import FAQSection from './FAQSection';
import CTASection from './CTASection';

export default function KaympusMarketingHome({ featuredBlogs = [] }) {
    const t = useT();

    return (
        // MotionConfig reducedMotion="user": every framer-motion animation on
        // the page collapses to a plain state change when the visitor's OS has
        // "reduce motion" on — landing.css already does the same for CSS
        // keyframes, this covers the JS-driven half.
        <MotionConfig reducedMotion="user">
        <div className="landing-root min-h-screen bg-[#FAFCFF]">
            {/* Keyboard / screen-reader shortcut past the header. Visually
                hidden until focused, then pops in above everything. */}
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100]
                           focus:bg-white focus:text-[#2040CF] focus:font-bold focus:text-sm
                           focus:px-4 focus:py-2.5 focus:rounded-lg focus:shadow-lg
                           focus:ring-2 focus:ring-[#2040CF]"
            >
                {t('common.skipToContent')}
            </a>
            <Preloader />
            <ScrollToHash />
            <Header />
            <main id="main-content">
                <ScrollLayout>
                    <AnimatedSection animationType="scale-up">
                        <Hero />
                    </AnimatedSection>

                    <AnimatedSection animationType="fade-in" delay={0.1}>
                        <TheProblem />
                    </AnimatedSection>

                    <AnimatedSection animationType="fade-in" delay={0.1}>
                        <WhyERPFail />
                    </AnimatedSection>

                    <AnimatedSection animationType="fade-in" delay={0.1}>
                        <ProductShowcase />
                    </AnimatedSection>

                    <AnimatedSection animationType="fade-in" delay={0.1}>
                        <ROICalculator />
                    </AnimatedSection>

                    <AnimatedSection animationType="fade-in" delay={0.1}>
                        <MultiSchool />
                    </AnimatedSection>

                    <AnimatedSection animationType="fade-in" delay={0.1}>
                        <TargetAudience />
                    </AnimatedSection>

                    <AnimatedSection animationType="fade-in" delay={0.1}>
                        <HowItWorks />
                    </AnimatedSection>

                    <AnimatedSection animationType="fade-in" delay={0.1}>
                        <FAQSection />
                    </AnimatedSection>

                    {/* Featured blog posts — data fetched server-side in the
                        home Server Component and passed in, so titles/links
                        are in the initial HTML (SEO). Renders nothing when
                        there are no featured posts. */}
                    <AnimatedSection animationType="fade-in" delay={0.1}>
                        <FeaturedBlogs posts={featuredBlogs} />
                    </AnimatedSection>

                    <AnimatedSection animationType="fade-in" delay={0.1}>
                        <CTASection />
                    </AnimatedSection>

                </ScrollLayout>
            </main>
            <Footer />
        </div>
        </MotionConfig>
    );
}

